/**
 * THE VOICE ORB, second generation: a bridge onto the vendored orb-ui themes.
 *
 * The first-generation orb was a 1200-line hand-rolled Canvas 2D renderer (it lives
 * in git history at this path). It has been replaced with the themes from
 * https://github.com/alexanderqchen/orb-ui, vendored under ./orb-ui/ — MIT, zero
 * runtime dependencies, modified where our constraints demanded it (each change is
 * marked `[voice-ai-platform]`; the inventory is in ./orb-ui/README.md).
 *
 * WHAT THIS FILE PRESERVES. The contract is unchanged, because two call sites and
 * the embed depend on it: a `state` in the same six-value union orb-ui happens to
 * share, a `levels()` GETTER read inside the animation frame (never a prop — see
 * the header of lib/call/meter.ts: a value changing 60×/s in React state would
 * re-render every subscriber 60×/s), `role="img"` with a state-derived label, and
 * a diameter in CSS pixels.
 *
 * WHICH THEME RENDERS. `cloud` by default — orb-ui's signature look, a WebGL
 * fragment-shader cloudscape — with `circle` (DOM + CSS) and `radial` (a WebGL
 * pinwheel) selectable per widget via the `theme` prop, which the embed exposes as
 * the `orbTheme` config key. That reverses this file's original "no WebGL in the
 * embed" rule, deliberately: the look is the reason for the swap. The old rule's
 * two risks are handled rather than ignored — if a WebGL context cannot be created
 * the bridge renders the `circle` theme instead (DOM + CSS, animates everywhere),
 * and the tiny inline orb in plain-button.tsx asks for `circle` explicitly so a
 * 20 px dot never claims one of the page's limited GPU contexts.
 *
 * WHAT THE FLOORS DO. `levels()` legitimately returns zeroes forever on the
 * fallback recorder path (no AudioContext — see types.ts). The themes map volume
 * to motion, so silence would read as a dead orb mid-call. The floors below carry
 * the first generation's trick forward: listening breathes slowly, speaking pulses
 * at roughly English syllable rate, and real audio rides on top of the floor
 * instead of being replaced by it.
 *
 * `colors` IS STILL ACCEPTED AND CURRENTLY DECORATIVE. The vendored themes paint
 * their own palettes (the cloud's periwinkle IS the look we vendored them for).
 * The prop stays in the type so no call site breaks and so a future tint pass has
 * somewhere to land. The embed already ignored stored `colors` (docs/issues.md,
 * issue 36), so for shipped widgets this is not a behaviour change.
 */
import { useCallback, useRef } from 'react'
import { CircleTheme } from './orb-ui/themes/circle'
import { CloudTheme } from './orb-ui/themes/cloud'
import { RadialTheme } from './orb-ui/themes/radial'

export type OrbState = 'idle' | 'connecting' | 'listening' | 'thinking' | 'speaking' | 'error'

/** The themes the bridge knows how to drive. `cloud` and `radial` are WebGL and
 *  fall back to `circle` where no context is available. The remaining vendored
 *  themes (bars, debug) are importable from ./orb-ui but are not wired to
 *  `levels()`. */
export type OrbCanvasTheme = 'cloud' | 'circle' | 'radial'

export interface OrbCanvasProps {
  state: OrbState
  /** Diameter in CSS pixels. Default 96. */
  size?: number
  /**
   * Read INSIDE the animation frame, never passed as a value.
   * Returns { user, assistant } each 0..1.
   */
  levels?: () => { user: number; assistant: number }
  /** Two-stop palette plus an optional specular accent. Accepted for API
   *  compatibility; the vendored themes currently paint their own palettes. */
  colors?: { from: string; to: string; accent?: string }
  /** Which vendored theme to render. Default `cloud`. The WebGL themes (`cloud`,
   *  `radial`) fall back to `circle` on pages where WebGL is unavailable. */
  theme?: OrbCanvasTheme
  className?: string
  /** Accessible label. Defaults to something derived from `state`. */
  ariaLabel?: string
}

const LABELS: Record<OrbState, string> = {
  idle: 'Voice assistant, idle',
  connecting: 'Connecting to the voice assistant',
  listening: 'Listening',
  thinking: 'Thinking',
  speaking: 'Assistant speaking',
  error: 'Voice assistant unavailable',
}

/** The cloud's resting dot, as a fraction of its surface. Upstream's launch dot is
 *  0.063 — right for their interactive control, a 4 px speck under our widgets.
 *  0.42 of a 112 px widget's surface is a 26 px resting presence. */
const RESTING_DOT_SCALE = 0.42

/**
 * Phase-driven motion floors, carried over from the first-generation orb.
 *
 * Listening is attentive and slow — a person being listened to is not making
 * anything happen. Speaking is syllabic: |sin| at 2.2 Hz gives 4.4 pulses a second,
 * roughly English syllable rate, under a slow phrase envelope that dips between
 * sentences. With real audio present the floor is a small offset and the motion is
 * genuinely the voice; with no meter it still reads as a live call from across the
 * room.
 */
function listenFloor(t: number): number {
  return 0.09 + 0.05 * (0.5 + 0.5 * Math.sin(t * 1.07)) + 0.03 * Math.sin(t * 1.63 + 1.1)
}

function speakFloor(t: number): number {
  const phrase = 0.35 + 0.65 * (0.5 + 0.5 * Math.sin(t * 1.34))
  const syllable = Math.pow(Math.abs(Math.sin(t * Math.PI * 2.2)), 1.4)
  return 0.12 + 0.34 * phrase * syllable
}

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value
}

/** Probed once per page, lazily. Cloud has its own static-gradient fallback, but a
 *  circle that animates beats a gradient that doesn't. */
let webglSupport: boolean | undefined
function webglAvailable(): boolean {
  if (webglSupport === undefined) {
    try {
      const probe = document.createElement('canvas')
      webglSupport = probe.getContext('webgl') !== null
    } catch {
      webglSupport = false
    }
  }
  return webglSupport
}

export function OrbCanvas(props: OrbCanvasProps) {
  const { state, size = 96, levels, theme = 'cloud', className, ariaLabel } = props
  // `colors` is intentionally not destructured: accepted, not yet painted.

  // Refs, so the getter below is referentially stable and the themes' frame loops
  // never re-subscribe. This is the same two-channel design as the old orb: state
  // through React, loudness around it.
  const stateRef = useRef(state)
  stateRef.current = state
  const levelsRef = useRef(levels)
  levelsRef.current = levels

  const getVolume = useCallback(() => {
    const s = stateRef.current
    if (s !== 'listening' && s !== 'speaking') return 0
    const read = levelsRef.current
    const level = read === undefined ? 0 : clamp01(s === 'speaking' ? read().assistant : read().user)
    const t = performance.now() / 1000
    const floor = s === 'speaking' ? speakFloor(t) : listenFloor(t)
    return floor + (1 - floor) * level
  }, [])

  const shared = {
    state,
    size,
    volume: 0, // unused: getVolume wins inside the themes' frame loops
    getVolume,
    className,
    interactive: false,
    role: 'img',
    'aria-label': ariaLabel ?? LABELS[state],
  }

  if (theme === 'cloud' && webglAvailable()) {
    return <CloudTheme {...shared} idleVisible idleScale={RESTING_DOT_SCALE} />
  }
  if (theme === 'radial' && webglAvailable()) {
    return <RadialTheme {...shared} />
  }
  // At 32 px and under the upstream 0.55 fill renders a speck; fill the box.
  return <CircleTheme {...shared} fill={size <= 32 ? 0.9 : undefined} />
}
